import { createListenerMiddleware } from "@reduxjs/toolkit";
import type { AppDispatch } from "../../app/store";
import { showError, showSuccess } from "../notifications/notificationsSlice";
import { isSuccessfulFinalStatus } from "./backgroundProcessStatus";
import { clearPersistent, processCompleted } from "./backgroundProcessingSlice";

/**
 * backgroundCompletionListener — the location-independent completion side effect.
 *
 * The poller emits `processCompleted` once per finished run (from anywhere — no
 * component needs to be mounted). This listener:
 *   1. releases the persistent subscription so the poller can stop, and
 *   2. shows the completion toast.
 *
 * Per-feature data refresh stays with the launching screen via its `onCompleted`
 * (it owns component-local state); when the user is elsewhere the data is
 * refetched on their return. Moving the refresh itself behind feature-specific
 * listeners is a later refinement (see doc/background-processing-architecture.md).
 */
export const backgroundCompletionMiddleware = createListenerMiddleware();

backgroundCompletionMiddleware.startListening({
  actionCreator: processCompleted,
  effect: (action, api) => {
    const dispatch = api.dispatch as AppDispatch;
    const { objectType, objectId, status } = action.payload;

    dispatch(clearPersistent({ objectType, objectId }));

    dispatch(
      isSuccessfulFinalStatus(status)
        ? showSuccess("background-process-completed-refreshing")
        : showError("background-process-failed"),
    );
  },
});
